import { useEffect } from "react";
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react";

export type ToastType = "success" | "error" | "warning" | "info";

interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose: () => void;
  title?: string;
}

export function Toast({
  message,
  type = "info",
  duration = 2000,
  onClose,
  title,
}: ToastProps) {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [duration, onClose]);

  const getToastStyles = () => {
    switch (type) {
      case "success":
        return {
          container: "bg-green-50 border-green-200",
          icon: <CheckCircle className="text-green-600" size={20} />,
          title: "text-green-800",
          message: "text-green-700",
          closeBtn: "text-green-500 hover:text-green-700",
        };
      case "error":
        return {
          container: "bg-red-50 border-red-200",
          icon: <AlertCircle className="text-red-600" size={20} />,
          title: "text-red-800",
          message: "text-red-700",
          closeBtn: "text-red-500 hover:text-red-700",
        };
      case "warning":
        return {
          container: "bg-amber-50 border-amber-200",
          icon: <AlertTriangle className="text-amber-600" size={20} />,
          title: "text-amber-800",
          message: "text-amber-700",
          closeBtn: "text-amber-500 hover:text-amber-700",
        };
      case "info":
      default:
        return {
          container: "bg-blue-50 border-blue-200",
          icon: <Info className="text-blue-600" size={20} />,
          title: "text-blue-800",
          message: "text-blue-700",
          closeBtn: "text-blue-500 hover:text-blue-700",
        };
    }
  };

  const styles = getToastStyles();

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg animate-slide-in-right ${styles.container}`}
      role="alert"
    >
      <div className="shrink-0 mt-0.5">{styles.icon}</div>
      <div className="flex-1 min-w-0">
        {title && (
          <p className={`font-semibold text-sm mb-1 ${styles.title}`}>
            {title}
          </p>
        )}
        <p className={`text-sm ${styles.message}`}>{message}</p>
      </div>
      <button
        onClick={onClose}
        className={`shrink-0 ${styles.closeBtn} transition-colors`}
        aria-label="Close notification"
      >
        <X size={18} />
      </button>
    </div>
  );
}
